/**
 * Unified API error contract (architecture.md §4) — zod-typed.
 *
 * Response body on error is ALWAYS:
 *   { "error": { "code": ApiErrorCode, "message": string, "details"?: unknown } }
 *
 * No legacy `{ response }` wrapper and no `{ success:false }` flag on the wire —
 * the new stack is clean REST with proper HTTP status codes (architecture.md §4,
 * "Контракт API — REST"). Backward parity with the old n8n shape
 * (`{ success:false, message }`) is provided separately by the adapters in
 * `./legacy.ts`, so a future caller migrating data never loses the message.
 *
 * The schema is the single source of truth: `ApiErrorBody` is *derived* from the
 * zod schema (`z.infer`), not hand-written, so the runtime validator and the
 * compile-time type can never drift. Shared between api (error handler,
 * response validation) and web (typed client / `normalize` on the consumer).
 */
import { z } from 'zod';
export const API_ERROR_CODES = [
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'NOT_FOUND',
    'VALIDATION_ERROR',
    'CONFLICT',
    'STRATEGY_UNAVAILABLE',
    'RATE_LIMITED',
    'INTERNAL',
    'SERVICE_UNAVAILABLE',
];
/** zod enum of the unified error codes (architecture.md §4). */
export const apiErrorCodeSchema = z.enum(API_ERROR_CODES);
/** Canonical HTTP status for each error code. */
export const API_ERROR_HTTP_STATUS = {
    UNAUTHENTICATED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    VALIDATION_ERROR: 400,
    CONFLICT: 409,
    STRATEGY_UNAVAILABLE: 409,
    RATE_LIMITED: 429,
    INTERNAL: 500,
    // A required backing service is not configured/reachable (e.g. no system
    // mailbox for password-reset email, E12-F06-T31). The request may succeed later.
    SERVICE_UNAVAILABLE: 503,
};
/**
 * Inner error payload. `message` is REQUIRED and non-empty — it is the minimum
 * contract every consumer can rely on (acceptance criterion: "ошибка содержит
 * как минимум поле сообщения"). `details` is free-form (zod issues, etc.).
 */
export const apiErrorDetailSchema = z.object({
    code: apiErrorCodeSchema,
    message: z.string().min(1),
    details: z.unknown().optional(),
});
/** Shape of the full error body returned to the client. */
export const apiErrorBodySchema = z.object({
    error: apiErrorDetailSchema,
});
/** Runtime type guard — is an unknown value a valid unified error body? */
export function isApiErrorBody(value) {
    return apiErrorBodySchema.safeParse(value).success;
}
/**
 * Is this error detail an "unauthorized" signal? True for the unified
 * `UNAUTHENTICATED` code (HTTP 401). This is the single predicate the web
 * `handleUnauthorized` flow branches on — parity with the legacy SPA, which
 * reacted whenever a normalized response carried `error === 'unauthorized'`
 * (the n8n router's session-invalid node). The legacy string is mapped to
 * `UNAUTHENTICATED` by `normalizeLegacyError` (see `./legacy.ts`), so both the
 * new REST 401 and an old-shape `{ success:false, message:'unauthorized' }`
 * collapse to the same code and trigger the same reaction.
 */
export function isUnauthorizedError(detail) {
    return detail.code === 'UNAUTHENTICATED';
}
/**
 * Application error. Thrown anywhere in the request lifecycle; the Fastify
 * error handler converts it into `ApiErrorBody` with the right HTTP status.
 */
export class ApiError extends Error {
    code;
    statusCode;
    /**
     * HTTP status as an OWN-ENUMERABLE data property (alias of `statusCode`).
     * `Error.prototype.message` is non-enumerable and `statusCode` was the only
     * status field, so a structural assertion like Vitest's `toMatchObject`
     * (which inspects own-enumerable keys only) could not see `{ message, status }`
     * on a thrown ApiError. Exposing `status` here — and re-declaring `message` as
     * an own-enumerable prop below — makes the error inspectable by tests and
     * consumers without changing the wire contract (the body still comes from
     * `toBody()`).
     */
    status;
    details;
    constructor(code, message, details) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.statusCode = API_ERROR_HTTP_STATUS[code];
        this.status = this.statusCode;
        this.details = details;
        // `Error` stores `message` non-enumerably on the prototype chain; re-define
        // it as an own-enumerable data prop so structural matchers (toMatchObject)
        // and JSON/log serialisation surface it consistently.
        Object.defineProperty(this, 'message', {
            value: message,
            enumerable: true,
            writable: true,
            configurable: true,
        });
    }
    toBody() {
        return {
            error: {
                code: this.code,
                message: this.message,
                ...(this.details !== undefined ? { details: this.details } : {}),
            },
        };
    }
    /** Rebuild an `ApiError` from a parsed unified error body (e.g. on the web client). */
    static fromBody(body) {
        return new ApiError(body.error.code, body.error.message, body.error.details);
    }
    static unauthenticated(message = 'Authentication required') {
        return new ApiError('UNAUTHENTICATED', message);
    }
    static forbidden(message = 'Access denied') {
        return new ApiError('FORBIDDEN', message);
    }
    static notFound(message = 'Resource not found') {
        return new ApiError('NOT_FOUND', message);
    }
    static validation(message = 'Validation failed', details) {
        return new ApiError('VALIDATION_ERROR', message, details);
    }
    static conflict(message = 'Conflict') {
        return new ApiError('CONFLICT', message);
    }
    static rateLimited(message = 'Too many requests') {
        return new ApiError('RATE_LIMITED', message);
    }
    static internal(message = 'Internal server error') {
        return new ApiError('INTERNAL', message);
    }
    static serviceUnavailable(message = 'Service unavailable') {
        return new ApiError('SERVICE_UNAVAILABLE', message);
    }
}
//# sourceMappingURL=errors.js.map